import { useState } from "react";
import { api, useAction, useApi } from "../../api.ts";
import { Button, Card, ErrorText, Field, Input, Loading, LoadError, PageHeader, useToast } from "../../ui/index.tsx";
import type { Settings as SettingsData } from "../../../shared/types.ts";

type NumKey = Exclude<keyof SettingsData, "discord_webhook_url">;
type Draft = Record<keyof SettingsData, string>;

interface NumField { key: NumKey; label: string; hint: string; min: number; max: number }

const SECTIONS: { title: string; description: string; fields: NumField[] }[] = [
  {
    title: "Sessions",
    description: "How long sign-ins last before the user has to log in again.",
    fields: [
      { key: "web_session_days", label: "Web session (days)", hint: "1-90", min: 1, max: 90 },
      { key: "loader_session_hours", label: "Loader session (hours)", hint: "1-168", min: 1, max: 168 },
    ],
  },
  {
    title: "Devices & HWID",
    description: "Limits on how many machines an account can use.",
    fields: [
      { key: "default_max_devices", label: "Default max devices", hint: "1-10. Applies to accounts without an override.", min: 1, max: 10 },
      { key: "hwid_reset_cooldown_days", label: "HWID reset cooldown (days)", hint: "0-365. 0 allows resets at any time.", min: 0, max: 365 },
    ],
  },
  {
    title: "Referrals",
    description: "Reward for bringing in a new user.",
    fields: [{ key: "referral_bonus_days", label: "Referral bonus (days)", hint: "0-90. 0 turns the bonus off.", min: 0, max: 90 }],
  },
];

const toDraft = (s: SettingsData): Draft => ({
  discord_webhook_url: s.discord_webhook_url,
  web_session_days: String(s.web_session_days),
  loader_session_hours: String(s.loader_session_hours),
  default_max_devices: String(s.default_max_devices),
  referral_bonus_days: String(s.referral_bonus_days),
  hwid_reset_cooldown_days: String(s.hwid_reset_cooldown_days),
});

export default function Settings() {
  const settings = useApi<SettingsData>("/admin/settings");
  return (
    <>
      <PageHeader title="Settings" description="Server-wide configuration. Changes apply immediately." />
      {!settings.data ? (
        settings.loading ? <Loading /> : <LoadError error={settings.error} retry={() => void settings.reload()} />
      ) : (
        <SettingsForm initial={settings.data} onSaved={settings.setData} />
      )}
    </>
  );
}

function SettingsForm({ initial, onSaved }: { initial: SettingsData; onSaved: (s: SettingsData) => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(() => toDraft(initial));
  const [localError, setLocalError] = useState<string | null>(null);

  const base = toDraft(initial);
  const keys = Object.keys(base) as (keyof SettingsData)[];
  const changed = keys.filter((k) => draft[k] !== base[k]);
  const dirty = changed.length > 0;

  const set = (k: keyof SettingsData, v: string) => { setDraft((d) => ({ ...d, [k]: v })); setLocalError(null); };

  const save = useAction(async () => {
    const patch: Partial<SettingsData> = {};
    for (const k of changed) {
      if (k === "discord_webhook_url") { patch[k] = draft[k].trim(); continue; }
      const n = Number(draft[k]);
      if (draft[k].trim() === "" || !Number.isInteger(n)) {
        setLocalError("Numeric settings must be whole numbers.");
        return;
      }
      patch[k] = n;
    }
    const next = await api.patch<SettingsData>("/admin/settings", patch);
    onSaved(next);
    setDraft(toDraft(next));
    toast("Settings saved", "success");
  });

  const discard = () => { setDraft(toDraft(initial)); setLocalError(null); save.setError(null); };

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save.run(); }} className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-2">
      {SECTIONS.map((sec) => (
        <Card key={sec.title} title={sec.title} description={sec.description}>
          <div className="grid gap-4 sm:grid-cols-2">
            {sec.fields.map((f) => (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <Input type="number" inputMode="numeric" min={f.min} max={f.max} step={1} value={draft[f.key]} onChange={(e) => set(f.key, e.target.value)} />
              </Field>
            ))}
          </div>
        </Card>
      ))}

      <Card title="Integrations" description="Where the server sends notifications.">
        <Field label="Discord webhook URL" hint="Leave empty to disable. Must start with https://discord.com/api/webhooks/. Treat it as a secret.">
          <Input type="url" value={draft.discord_webhook_url} onChange={(e) => set("discord_webhook_url", e.target.value)}
            placeholder="https://discord.com/api/webhooks/…" className="font-mono text-xs" autoComplete="off" spellCheck={false} />
        </Field>
      </Card>
      </div>

      <ErrorText>{localError ?? save.error}</ErrorText>
      <div className="flex items-center gap-2">
        <Button variant="primary" type="submit" icon="check" loading={save.pending} disabled={!dirty}>Save changes</Button>
        <Button variant="ghost" onClick={discard} disabled={!dirty || save.pending}>Discard</Button>
        {dirty && <span className="text-xs text-faint">{changed.length} unsaved {changed.length === 1 ? "change" : "changes"}</span>}
      </div>
    </form>
  );
}
